import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { DollarSign } from 'lucide-react';
import { OpNoteCptCode, OpNoteFinancialImpact } from '../opNoteResponseTypes';

interface RevenueImpactCardProps {
  primaryCpt: OpNoteCptCode;
  additionalCpts: OpNoteCptCode[];
  financialImpact: OpNoteFinancialImpact;
}

export function RevenueImpactCard({ primaryCpt, additionalCpts, financialImpact }: RevenueImpactCardProps) {
  const allCpts = [primaryCpt, ...additionalCpts].filter(c => c.code);

  return (
    <Card className="border-border/60">
      <CardContent className="p-5 space-y-3">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <DollarSign className="w-3.5 h-3.5" /> Revenue Impact
        </h4>

        {financialImpact.summary_text && (
          <p className="text-xs text-muted-foreground italic">{financialImpact.summary_text}</p>
        )}

        <div className="space-y-2">
          {allCpts.map((cpt, i) => (
            <div key={`${cpt.code}-${i}`} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className="font-mono font-semibold">{cpt.code}</span>
                {cpt.modifiers?.map(m => (
                  <Badge key={m} variant="secondary" className="text-[10px] h-4">{m}</Badge>
                ))}
              </div>
              <span className="text-xs text-muted-foreground truncate max-w-[160px]">{cpt.description}</span>
            </div>
          ))}
        </div>

        <Separator />

        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Facility Revenue</span>
            <span>${financialImpact.facility_revenue.toLocaleString()}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Professional Revenue</span>
            <span>${financialImpact.professional_revenue.toLocaleString()}</span>
          </div>
          {financialImpact.modifier_22_upcharge ? (
            <div className="flex items-center justify-between text-xs text-amber-600 font-medium">
              <span>Mod -22 Upcharge</span>
              <span>+${financialImpact.modifier_22_upcharge.toLocaleString()}</span>
            </div>
          ) : null}
        </div>

        <Separator />

        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-foreground">Total Estimated Payout</span>
          <span className="text-xl font-bold text-primary tracking-tight">
            ${financialImpact.total_estimated_payout.toLocaleString()}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
